<template>
  <div class="container grading-scale-management-container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة مقاييس التقدير</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة مقياس جديد
          </el-button>
        </div>
      </template>

      <!-- --- الشرح الجديد هنا --- -->
      <el-collapse v-model="activeCollapse" style="margin-bottom: 20px;">
        <el-collapse-item name="1">
            <template #title>
                <el-icon><QuestionFilled /></el-icon><span style="margin-right: 8px; font-weight: bold;">لماذا هذه الواجهة مهمة؟ (اضغط للتوضيح)</span>
            </template>
            <div>
                <p>هذه الواجهة تسمح لك بتعريف "مفتاح التقديرات" الذي تستخدمه المدرسة. هنا، تقوم بربط الدرجات الرقمية (النسب المئوية) بتقديرات نصية (مثل "ممتاز"، "جيد جداً"، "A+", "B").</p>
                <p><strong>مثال:</strong> يمكنك تعريف أن أي طالب يحصل على درجة بين 90% و 100% سيتم منحه تقدير "ممتاز" أو "A+".</p>
                <p><strong>الأهمية:</strong> هذا المقياس هو الذي سيستخدمه النظام تلقائيًا في جميع الشهادات والتقارير الأكاديمية لعرض التقدير المناسب بجانب درجة الطالب، مما يجعل التقارير أكثر وضوحًا لأولياء الأمور والطلاب.</p>
            </div>
        </el-collapse-item>
      </el-collapse>

      <!-- فلتر السنة الدراسية -->
      <el-form label-position="top">
          <el-form-item label="اختر السنة الدراسية لعرض مقاييس التقدير الخاصة بها">
              <el-select v-model="selectedStudyYear" placeholder="اختر السنة الدراسية" @change="fetchGradingScales" clearable style="width: 100%;">
                  <el-option v-for="year in studyYears" :key="year.id" :label="year.name" :value="year.id" />
              </el-select>
          </el-form-item>
      </el-form>
      
      <el-divider v-if="selectedStudyYear" />

      <!-- جدول عرض مقاييس التقدير -->
      <el-table :data="gradingScales" v-loading="loading" border style="width: 100%" v-if="selectedStudyYear">
        <el-table-column prop="grade_label" label="التقدير" width="120" />
        <el-table-column label="نطاق الدرجات (%)" width="180">
            <template #default="{ row }">
                {{ row.min_percentage }}% - {{ row.max_percentage }}%
            </template>
        </el-table-column>
        <!-- --- التعديل الرئيسي هنا: إضافة fallback للوصف --- -->
        <el-table-column label="الوصف">
            <template #default="{ row }">
                {{ row.description || '—' }}
            </template>
        </el-table-column>
        <el-table-column prop="gpa_point" label="نقاط المعدل" width="120" />
        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm
              title="هل أنت متأكد من حذف هذا المقياس؟"
              confirm-button-text="نعم"
              cancel-button-text="لا"
              @confirm="handleDelete(row.id)"
            >
              <template #reference><el-button size="small" type="danger">حذف</el-button></template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
       <div v-else>
        <el-empty description="الرجاء اختيار السنة الدراسية لعرض مقاييس التقدير." />
      </div>
    </el-card>

    <!-- Dialog لإنشاء/تعديل المقياس -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="50%" :close-on-click-modal="false">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="السنة الدراسية" prop="study_year_id">
            <el-select v-model="form.study_year_id" placeholder="مطلوب" style="width:100%" disabled>
                <el-option v-for="item in studyYears" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
        </el-form-item>
        <el-row :gutter="20">
            <el-col :span="12">
                <el-form-item label="التقدير" prop="grade_label">
                    <el-input v-model="form.grade_label" placeholder="مثال: A+, ممتاز" />
                </el-form-item>
            </el-col>
            <el-col :span="12">
                <el-form-item label="الوصف" prop="description">
                    <el-input v-model="form.description" placeholder="مثال: Outstanding, Excellent" />
                </el-form-item>
            </el-col>
        </el-row>
        <el-row :gutter="20">
            <el-col :span="12">
                <el-form-item label="الحد الأدنى للنسبة المئوية (%)" prop="min_percentage">
                    <el-input-number v-model="form.min_percentage" :min="0" :max="100" :precision="2" :step="0.1" style="width:100%;" />
                </el-form-item>
            </el-col>
            <el-col :span="12">
                 <el-form-item label="الحد الأعلى للنسبة المئوية (%)" prop="max_percentage">
                    <el-input-number v-model="form.max_percentage" :min="0" :max="100" :precision="2" :step="0.1" style="width:100%;" />
                </el-form-item>
            </el-col>
        </el-row>
        <el-row :gutter="20">
             <el-col :span="12">
                <el-form-item label="نقاط المعدل التراكمي (GPA)" prop="gpa_point">
                    <el-input-number v-model="form.gpa_point" :min="0" :max="5" :precision="2" :step="0.1" style="width:100%;" />
                </el-form-item>
             </el-col>
             <el-col :span="12">
                <el-form-item label="ترتيب التقدير (الأعلى هو 1)" prop="rank_order">
                    <el-input-number v-model="form.rank_order" :min="1" :step="1" style="width:100%;" />
                </el-form-item>
             </el-col>
        </el-row>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">
            {{ form.id ? 'تحديث' : 'إنشاء' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus, QuestionFilled } from '@element-plus/icons-vue';

export default {
  name: 'GradingScaleManagement',
  components: { Plus, ElPopconfirm, QuestionFilled },
  data() {
    return {
      api: null,
      loading: false,
      gradingScales: [],
      studyYears: [],
      selectedStudyYear: null,
      
      dialogVisible: false,
      formLoading: false,
      form: this.getInitialForm(),
      activeCollapse: '',

      rules: {
        study_year_id: [{ required: true, message: 'السنة الدراسية مطلوبة', trigger: 'change' }],
        grade_label: [{ required: true, message: 'التقدير مطلوب', trigger: 'blur' }],
        min_percentage: [{ required: true, message: 'الحد الأدنى مطلوب', trigger: 'blur' }],
        max_percentage: [{ required: true, message: 'الحد الأعلى مطلوب', trigger: 'blur' }],
        rank_order: [{ required: true, message: 'الترتيب مطلوب', trigger: 'blur' }],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل مقياس التقدير' : 'إنشاء مقياس تقدير جديد';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchStudyYears();
  },
  methods: {
    getInitialForm() {
      return { 
        id: null, 
        study_year_id: this.selectedStudyYear,
        grade_label: '', 
        min_percentage: 0,
        max_percentage: 100,
        description: '',
        gpa_point: 0.0,
        rank_order: null,
      };
    },
    async fetchStudyYears() {
        try {
          const response = await this.api.get('/study-years');
          this.studyYears = response.data.data || response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الأعوام الدراسية.');
        }
    },
    async fetchGradingScales() {
      if (!this.selectedStudyYear) {
          this.gradingScales = [];
          return;
      }
      this.loading = true;
      try {
        const response = await this.api.get('/grading-scales', { params: { study_year_id: this.selectedStudyYear }});
        this.gradingScales = response.data.data || response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب مقاييس التقدير.');
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      if (!this.selectedStudyYear) {
        ElMessage.warning('الرجاء اختيار سنة دراسية أولاً.');
        return;
      }
      this.form = this.getInitialForm();
      this.dialogVisible = true;
    },
    handleEdit(scale) {
      this.form = { ...scale };
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        try {
          if (this.form.id) {
            await this.api.put(`/grading-scales/${this.form.id}`, this.form);
            ElMessage.success('تم تحديث المقياس بنجاح!');
          } else {
            await this.api.post('/grading-scales', this.form);
            ElMessage.success('تم إنشاء المقياس بنجاح!');
          }
          this.dialogVisible = false;
          this.fetchGradingScales();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/grading-scales/${id}`);
        ElMessage.success('تم حذف المقياس بنجاح!');
        this.fetchGradingScales();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || 'فشل في حذف المقياس.');
      }
    },
  },
};
</script>

<style scoped>
.grading-scale-management-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}
.el-collapse-item p {
  line-height: 1.6;
}
</style>
